import { Component, type ErrorInfo, type ReactNode } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

// ─── Error Fallback UIs ────────────────────────────────────────────────────────

const RefreshIcon = () => (
    <Box
        component="svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        sx={{ width: 16, height: 16 }}
        data-testid="error-boundary-box-polyline">
        <polyline points="23 4 23 10 17 10" data-testid="error-boundary-polyline-1" />
        <polyline points="1 20 1 14 7 14" data-testid="error-boundary-polyline-2" />
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" data-testid="error-boundary-path-1" />
    </Box>
)

const AlertTriangleIcon = ({ size = 40 }: { size?: number }) => (
    <Box
        component="svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        sx={{ width: size, height: size, color: 'warning.main' }}
        data-testid="error-boundary-box-path">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" data-testid="error-boundary-path-2" />
        <line x1="12" y1="9" x2="12" y2="13" data-testid="error-boundary-line-1" />
        <line x1="12" y1="17" x2="12.01" y2="17" data-testid="error-boundary-line-2" />
    </Box>
)

/** Full-page error fallback — used at route level */
export const PageErrorFallback = ({ error, resetError }: { error: Error | null; resetError: () => void }) => {
    // eslint-disable-next-line turbo/no-undeclared-env-vars -- Vite built-in, not a user env var
    const isDev = import.meta.env.DEV

    return (
        <Stack
            alignItems="center"
            justifyContent="center"
            spacing={3}
            sx={{ minHeight: '60vh', px: 3, py: 8 }}
            data-testid="error-boundary-stack-alert-triangle-icon-1">
            <AlertTriangleIcon size={48} data-testid="error-boundary-alert-triangle-icon-1" />
            <Stack alignItems="center" spacing={1} data-testid="error-boundary-stack-typography">
                <Typography variant="h6" fontWeight={700} color="text.primary" data-testid="error-boundary-typography-1">
                    เกิดข้อผิดพลาด
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center" maxWidth={400} data-testid="error-boundary-typography-2">
                    หน้านี้เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้งหรือกลับไปหน้าก่อนหน้า
                </Typography>
            </Stack>
            {isDev && error && (
                <Alert
                    severity="error"
                    sx={{
                        width: '100%',
                        maxWidth: 600,
                        '& .MuiAlert-message': { width: '100%' }
                    }}
                    data-testid="error-boundary-alert">
                    <Typography
                        variant="caption"
                        component="pre"
                        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                        data-testid="error-boundary-typography-3">
                        {error.message}
                        {error.stack ? `\n\n${error.stack}` : ''}
                    </Typography>
                </Alert>
            )}
            <Stack direction="row" spacing={2} data-testid="error-boundary-stack-button">
                <Button variant="outlined" size="small" onClick={() => window.history.back()} data-testid="error-boundary-button-1">
                    ← กลับหน้าก่อน
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<RefreshIcon data-testid="error-boundary-refresh-icon-1" />}
                    onClick={resetError}
                    data-testid="error-boundary-button-reset-error-1">
                    ลองใหม่
                </Button>
                <Button variant="text" size="small" onClick={() => (window.location.href = '/')} data-testid="error-boundary-button-2">
                    หน้าแรก
                </Button>
            </Stack>
        </Stack>
    )
}

/** Inline/compact error fallback — used inside modules/widgets */
export const ModuleErrorFallback = ({ error, resetError }: { error: Error | null; resetError: () => void }) => {
    // eslint-disable-next-line turbo/no-undeclared-env-vars -- Vite built-in, not a user env var
    const isDev = import.meta.env.DEV

    return (
        <Stack
            spacing={1.5}
            p={3}
            sx={{
                border: '1px solid',
                borderColor: 'error.200',
                borderRadius: 2,
                bgcolor: 'error.50'
            }}
            data-testid="error-boundary-stack-alert-triangle-icon-2">
            <Stack direction="row" alignItems="center" spacing={1} data-testid="error-boundary-stack-alert-triangle-icon-3">
                <AlertTriangleIcon size={20} data-testid="error-boundary-alert-triangle-icon-2" />
                <Typography variant="body2" fontWeight={600} color="error.main" data-testid="error-boundary-typography-module">
                    โหลด module ไม่สำเร็จ
                </Typography>
            </Stack>
            {isDev && error && (
                <Collapse in data-testid="error-boundary-collapse">
                    <Typography
                        variant="caption"
                        component="pre"
                        sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            color: 'error.dark',
                            fontSize: 11
                        }}
                        data-testid="error-boundary-typography-4">
                        {error.message}
                    </Typography>
                </Collapse>
            )}
            <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<RefreshIcon data-testid="error-boundary-refresh-icon-2" />}
                onClick={resetError}
                sx={{ alignSelf: 'flex-start' }}
                data-testid="error-boundary-button-reset-error-2">
                ลองใหม่
            </Button>
        </Stack>
    )
}

// ─── Error Boundary Class ──────────────────────────────────────────────────────

interface Props {
    children: ReactNode
    fallback?: 'page' | 'module'
    /** เปลี่ยน key เพื่อ reset boundary โดยอัตโนมัติ (เช่น เปลี่ยน route) */
    resetKey?: string
}

interface State {
    hasError: boolean
    error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
    state: State = { hasError: false, error: null }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error }
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error('[ErrorBoundary]', error, info.componentStack)
    }

    componentDidUpdate(prevProps: Props) {
        if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
            this.setState({ hasError: false, error: null })
        }
    }

    resetError = () => {
        this.setState({ hasError: false, error: null })
    }

    render() {
        if (this.state.hasError) {
            const FallbackComponent = this.props.fallback === 'module' ? ModuleErrorFallback : PageErrorFallback
            return <FallbackComponent error={this.state.error} resetError={this.resetError} data-testid="error-boundary-fallback-component" />
        }
        return this.props.children
    }
}

export default ErrorBoundary
